import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MACHINERY_PRODUCTS } from "@/polymet/data/machinery-products";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchIcon } from "lucide-react";
import SearchResultItem from "@/polymet/components/search-result-item";
import { useAnalytics } from "@/polymet/hooks/use-analytics";

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const category = searchParams.get("category") || "";
  const condition = searchParams.get("condition") || "";
  const sortBy = searchParams.get("sort") || "relevance";
  const page = parseInt(searchParams.get("page") || "1", 10);
  
  const [searchResults, setSearchResults] = useState<typeof MACHINERY_PRODUCTS>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const { trackSearch, trackCategoryVisit } = useAnalytics();
  
  const ITEMS_PER_PAGE = 10;
  
  useEffect(() => {
    const performSearch = async () => {
      setLoading(true);
      
      // Simulate a search API call using the sample data
      let results = [...MACHINERY_PRODUCTS];
      
      // Filter by search query
      if (query) {
        const searchTerms = query.toLowerCase().split(" ");
        results = results.filter(product => {
          const searchText = `${product.name} ${product.manufacturer} ${product.model} ${product.description}`.toLowerCase();
          return searchTerms.every(term => searchText.includes(term));
        });
      }
      
      // Filter by category
      if (category) {
        results = results.filter(product => product.categoryId === category || product.subCategoryId === category || product.productCategoryId === category);
      }
      
      // Filter by condition
      if (condition) {
        results = results.filter(product => product.condition === condition);
      }
      
      // Sort results
      switch (sortBy) {
        case "price_low":
          results.sort((a, b) => a.price - b.price);
          break;
        case "price_high":
          results.sort((a, b) => b.price - a.price);
          break;
        case "newest":
          results.sort((a, b) => new Date(b.listedDate).getTime() - new Date(a.listedDate).getTime());
          break;
        case "rating":
          results.sort((a, b) => b.rating - a.rating);
          break;
        // Default is relevance, which is the original order in this demo
      }
      
      setTotalResults(results.length);
      
      // Paginate results
      const startIndex = (page - 1) * ITEMS_PER_PAGE;
      results = results.slice(startIndex, startIndex + ITEMS_PER_PAGE);
      
      setSearchResults(results);
      setLoading(false);

      // Track the search in analytics
      if (query.trim()) {
        try {
          await trackSearch({
            query: query.trim(),
            resultsCount: results.length,
            filters: {
              category,
              condition,
              sortBy,
              page
            },
            searchSource: 'header'
          });
        } catch (error) {
          console.error('Error tracking search:', error);
        }
      }
    };
    
    performSearch();
  }, [query, category, condition, sortBy, page, trackSearch]);
  
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const searchQuery = formData.get("search") as string;
    
    const newParams = new URLSearchParams(searchParams);
    newParams.set("q", searchQuery);
    newParams.set("page", "1"); // Reset to first page on new search
    setSearchParams(newParams);
  };
  
  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(key, value);
    newParams.set("page", "1"); // Reset to first page on filter change
    setSearchParams(newParams);
  };

  // Handle category click tracking
  const handleCategoryClick = async (categoryId: string, categoryType: 'category' | 'subcategory' | 'product_category') => {
    try {
      await trackCategoryVisit({
        categoryId: categoryType === 'category' ? categoryId : undefined,
        subcategoryId: categoryType === 'subcategory' ? categoryId : undefined,
        productCategoryId: categoryType === 'product_category' ? categoryId : undefined,
        visitType: categoryType,
        source: 'search'
      });
    } catch (error) {
      console.error('Error tracking category visit:', error);
    }
  };
  
  const totalPages = Math.ceil(totalResults / ITEMS_PER_PAGE);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Search Results</h1>
      
      <div className="mb-6">
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input 
            name="search"
            defaultValue={query}
            placeholder="Search machinery, equipment, and parts..." 
            className="flex-1"
          />
          <Button type="submit">
            <SearchIcon className="h-4 w-4 mr-2" />
            Search
          </Button>
        </form>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="md:col-span-1">
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">Category</h3>
              <Select value={category} onValueChange={(value) => updateFilter("category", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="construction">Construction</SelectItem>
                    <SelectItem value="earthmoving">Earthmoving</SelectItem>
                    <SelectItem value="excavators">Excavators</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <h3 className="font-medium mb-2">Condition</h3>
              <Select value={condition} onValueChange={(value) => updateFilter("condition", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Any Condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="any">Any Condition</SelectItem>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="Used">Used</SelectItem>
                    <SelectItem value="Refurbished">Refurbished</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <h3 className="font-medium mb-2">Sort By</h3>
              <Select value={sortBy} onValueChange={(value) => updateFilter("sort", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Relevance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="relevance">Relevance</SelectItem>
                    <SelectItem value="price_low">Price: Low to High</SelectItem>
                    <SelectItem value="price_high">Price: High to Low</SelectItem>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        <div className="md:col-span-3">
          <div className="mb-4 flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {loading ? (
                "Searching..."
              ) : (
                `Showing ${searchResults.length} of ${totalResults} results`
              )}
            </p>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Searching for products...</p>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="text-center py-12 border rounded-md">
              <p className="text-xl font-medium">No results found</p>
              <p className="text-muted-foreground mt-2">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="space-y-4">
              {searchResults.map((product) => (
                <SearchResultItem
                  key={product.id}
                  id={product.id}
                  name={product.name}
                  description={product.description}
                  image={product.images[0]}
                  price={product.price}
                  currency={product.currency}
                  condition={product.condition}
                  category={product.productCategoryId}
                  year={product.year}
                  manufacturer={product.manufacturer}
                  location={product.location}
                  rating={product.rating}
                  reviewCount={product.reviewCount}
                  supplierName={product.seller.name}
                  supplierSlug={product.seller.name.toLowerCase().replace(/\s+/g, '-')}
                  featured={product.featured}
                />
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="mt-6">
              <PaginationContent>
                {page > 1 && (
                  <PaginationItem>
                    <PaginationPrevious 
                      href={`?${new URLSearchParams({
                        ...Object.fromEntries(searchParams.entries()),
                        page: (page - 1).toString()
                      })}`} 
                    />
                  </PaginationItem>
                )}
                
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  // Show pages around current page
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        href={`?${new URLSearchParams({
                          ...Object.fromEntries(searchParams.entries()),
                          page: pageNum.toString()
                        })}`}
                        isActive={page === pageNum}
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                
                {page < totalPages && (
                  <PaginationItem>
                    <PaginationNext 
                      href={`?${new URLSearchParams({
                        ...Object.fromEntries(searchParams.entries()),
                        page: (page + 1).toString()
                      })}`} 
                    />
                  </PaginationItem>
                )}
              </PaginationContent>
            </Pagination>
          )}
        </div>
      </div>
    </div>
  );
}
